import { useState, useCallback, useEffect } from 'react';
import { type Process, type Deal, type Activities, type Comments, type CommentFormData, type ActivityFormData } from '../types/deal';
import { dealService } from '../services/domain/dealService';
import type { User } from '../types/deal';

interface UseDealsResult {
  data: Deal | null;
  processes: Process[];
  activities: Activities[];
  users: User[];
  comments: Comments[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
  createComment: (formData: CommentFormData) => Promise<{ success: boolean, message?: string }>;
  createActivity: (formData: ActivityFormData) => Promise<{ success: boolean, message?: string }>;
  updateStatus: (id: number, deal_process_id: number, deal_status_id: number) => Promise<{ success: boolean, message?: string }>;
  updateDeal: (formData: Deal) => Promise<{ success: boolean, message?: string }>;
  createAssign: (deal_id: number, user_id: number) => Promise<{ success: boolean, message?: string }>;
  updateAssign: (deal_id: number, user_id: number) => Promise<{ success: boolean, message?: string }>;
  updating: boolean;
  searching: boolean;
}

interface DealParams {
  id: number | null;
}

export const useDealsDetail = (params: DealParams): UseDealsResult => {
  const [data, setData] = useState<Deal | null>(null);
  const [processes, setProcesses] = useState<Process[]>([]);
  const [activities, setActivities] = useState<Activities[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [comments, setComments] = useState<Comments[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [updating] = useState(false);
  const [searching] = useState(false);

  const fetchProcesses = useCallback(async () => {
    try {
      setError(null);
      const response = await dealService.getProcesses();
      
      const sortedProcesses = response.data.sort((a: any, b: any) => a.process_order - b.process_order);
      setProcesses(sortedProcesses);

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error desconocido';
      setError(errorMessage);
      console.error('Error fetching procesos:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchUsers = useCallback(async () => {
    try {
      setError(null);
      const response = await dealService.getUsers();
      setUsers(response.data.users);

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error desconocido';
      setError(errorMessage);
      console.error('Error fetching procesos:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchActivities = useCallback(async () => {
    try {
      if (!params.id || params.id <= 0) {
        console.error('No hay ID válido para cargar');
        return;
      }
      setError(null);
      const response = await dealService.getActivities(params.id);

      setActivities(response.data)

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error desconocido';
      setError(errorMessage);
      console.error('Error fetching procesos:', err);
    } finally {
      setLoading(false);
    }
  }, [params.id]);

  const fetchComments = useCallback(async () => {
    try {
      if (!params.id || params.id <= 0) {
        console.error('No hay ID válido para cargar');
        return;
      }
      setError(null);
      const response = await dealService.getComments(params.id);

      setComments(response.data)

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error desconocido';
      setError(errorMessage);
      console.error('Error fetching procesos:', err);
    } finally {
      setLoading(false);
    }
  }, [params.id]);

  const fetchDeal = useCallback(async () => {
    // Verificar que existe un ID válido
    if (!params.id || params.id <= 0) {
      console.error('No hay ID válido para cargar');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await dealService.getDealsbyId(params.id);
      if (response) {
        setData(response);
      }

      await fetchProcesses();
      await fetchActivities();
      await fetchComments();
      await fetchUsers();
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error desconocido';
      setError(errorMessage);
      console.error('Error fetching deal:', err);
    } finally {
      setLoading(false);
    }
  }, [params.id, fetchProcesses]);

  useEffect(() => {
    if (params.id && params.id > 0) {
      fetchDeal();
    }
  }, [params.id, fetchDeal]);

  const refetch = useCallback(async () => {
    try {
      setLoading(true)
      setError(null);
    }
    catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error en actualizar';
      setError(errorMessage);
    } finally {
      setLoading(false)
    }
  }, [fetchDeal]);

  const createComment = useCallback(async (
    formData: CommentFormData
  ): Promise<{ success: boolean }> => {
    try {
      setError(null);

      const personResult = await dealService.createComment(formData);
      if (personResult.error) {
        setError(`Error al crear persona: ${personResult.error}`);
      }

      await fetchComments();

      return {
        success: true,
        // personId: personId
      };

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error en el proceso completo';
      setError(errorMessage);
      console.error('Error creating person complete:', err);

      return {
        success: false
      };
    } finally {

    }
  }, [fetchDeal]);

  const createActivity = useCallback(async (
    formData: ActivityFormData
  ) => {
    try {
      setError(null);

      const result = await dealService.createActivity(formData);
      if (result.error) {
        setError(`Error al crear persona: ${result.error}`);
      }

      await fetchActivities();

      return {
        success: true,
        // personId: personId
      };

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error en el proceso completo';
      setError(errorMessage);
      console.error('Error creating person complete:', err);

      return {
        success: false,
        error: errorMessage
      };
    } finally {

    }
  }, [fetchDeal]);


  const updateStatus = useCallback(async (
    id: number, deal_process_id: number, deal_status_id: number
  ) => {
    try {
      setError(null);

      const result = await dealService.updateStatus(id, deal_process_id, deal_status_id);
      if (result.error) {
        setError(`Error al crear persona: ${result.error}`);
      }

      await fetchDeal();

      return {
        success: true,
        // personId: personId
      };

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error en el proceso completo';
      setError(errorMessage);
      console.error('Error creating person complete:', err);

      return {
        success: false,
        error: errorMessage
      };
    } finally {

    }
  }, [fetchDeal]);

  const updateDeal = useCallback(async (
    formData: Deal
  ) => {
    try {
      setError(null);

      const result = await dealService.updateDeal(formData);
      if (result.error) {
        setError(`Error al crear persona: ${result.error}`);
      }

      await fetchDeal();

      return {
        success: true,
        // personId: personId
      };

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error en el proceso completo';
      setError(errorMessage);
      console.error('Error creating person complete:', err);

      return {
        success: false,
        error: errorMessage
      };
    } finally {

    }
  }, [fetchDeal]);

  const createAssign = useCallback(async (
      deal_id: number, user_id: number
    ) => {
      try {
        setError(null);
        const result = await dealService.createAsesor(deal_id, user_id);
        if (result.error) {
          setError(`Error al crear lead: ${result.error}`);
          return {
            success: false,
          };
        }
  
        await fetchDeal();
  
        return {
          success: true,
        };
  
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Error en el proceso completo';
        setError(errorMessage);
        return {
          success: false,
          error: errorMessage
        };
      } finally {
      }
    }, [fetchDeal]);
  
    const updateAssign = useCallback(async (
      deal_id: number, user_id: number
    ) => {
      try {
        setError(null);
        const result = await dealService.updateAsesor(deal_id, user_id);
        if (result.error) {
          setError(`Error al actualizar asignado ${result.error}`);
          return {
            success: false,
          };
        }
  
        await fetchDeal();
  
        return {
          success: true,
        };
  
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Error en el proceso completo';
        setError(errorMessage);
        return {
          success: false,
          error: errorMessage
        };
      } finally {
      }
    }, [fetchDeal]);




  return {
    data,
    processes,
    activities,
    users,
    comments,
    loading,
    error,
    refetch,
    createComment,
    createActivity,
    updateStatus,
    updateDeal,
    createAssign,
    updateAssign,
    updating,
    searching
  };
};